import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { addDays } from 'date-fns/addDays';
import { subDays } from 'date-fns/subDays';
import { startOfWeek } from 'date-fns/startOfWeek';
import { endOfWeek } from 'date-fns/endOfWeek';
import { startOfMonth } from 'date-fns/startOfMonth';
import { endOfMonth } from 'date-fns/endOfMonth';
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval';
import { eachWeekOfInterval } from 'date-fns/eachWeekOfInterval';
import { eachMonthOfInterval } from 'date-fns/eachMonthOfInterval';
import { addMonths } from 'date-fns/addMonths';
import { formatDate as formatDateFns } from 'date-fns/format';
import { getCanonicalDate } from 'uplisting-frontend/services/calendar/-internals/utils';
import { storage, StorageKey } from 'uplisting-frontend/utils/storage';
import {
  type WeekStart,
  WEEK_START_SUNDAY,
} from 'uplisting-frontend/utils/week-start';

export const CALENDAR_DATES_PER_PAGE_MONTH = 31;

export const CALENDAR_DATES_PER_PAGE_LARGE = 21;
export const CALENDAR_DATES_PER_PAGE_MEDIUM = 14;
export const CALENDAR_DATES_PER_PAGE_SMALL = 7;

export interface ICalendarViewport {
  startDate: Date;
  endDate: Date;
  dates: Date[];
}

export interface INavigationDates {
  today: Date;
  previousWeek: Date;
  nextWeek: Date;
  previousRange: Date;
  nextRange: Date;
}

export interface IMonthGroup {
  monthLabel: string;
  month: Date;
  weeks: Date[][];
}

export interface IMonthlyCalendarViewport extends ICalendarViewport {
  monthGroups: IMonthGroup[];
}

export default class CalendarUtilsService extends Service {
  @service screen!: Services['screen'];

  // used to make reactivity work with the local storage data
  @tracked recalculateWeekStartsOn = true;

  @cached
  get weekStartsOn(): WeekStart {
    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.recalculateWeekStartsOn;

    return (
      storage.local.get<0 | 1>(StorageKey.CALENDAR_WEEK_STARTS_ON) ??
      WEEK_START_SUNDAY
    );
  }

  set weekStartsOn(weekStart: WeekStart) {
    storage.local.set(StorageKey.CALENDAR_WEEK_STARTS_ON, weekStart);

    this.recalculateWeekStartsOn = !this.recalculateWeekStartsOn;
  }

  @cached
  get datesPerPage(): number {
    if (this.screen.isSmallScreen) {
      return CALENDAR_DATES_PER_PAGE_SMALL;
    }

    if (this.screen.isLargeOrBelow) {
      return CALENDAR_DATES_PER_PAGE_MEDIUM;
    }

    return CALENDAR_DATES_PER_PAGE_LARGE;
  }

  /**
   * create a calendar viewport with all necessary date information
   */
  public createCalendarViewport(
    startDate: Date,
    length = this.datesPerPage,
  ): ICalendarViewport {
    const dates = this.#generateDateRange(startDate, length);

    return {
      startDate: dates[0] as Date,
      endDate: dates[dates.length - 1] as Date,
      dates,
    };
  }

  /**
   * navigation dates for calendar controls
   */
  public getNavigationDates(currentDate?: Date): INavigationDates {
    const date = currentDate ?? new Date();

    return {
      today: date,
      previousWeek: subDays(date, 7),
      nextWeek: addDays(date, 7),
      previousRange: subDays(date, this.datesPerPage),
      nextRange: addDays(date, this.datesPerPage),
    };
  }

  public createMonthlyCalendarViewport(
    date: Date,
    numberOfMonths: number,
  ): IMonthlyCalendarViewport {
    const dateRange = this.#calculateDateRange(date, numberOfMonths);
    const months = eachMonthOfInterval(dateRange);
    const monthGroups = months.map((month) => this.#createMonthGroup(month));

    const dates = this.#flattenMonthGroupsToDates(monthGroups);

    return {
      monthGroups,
      dates,
      startDate: dates[0] as Date,
      endDate: dates[dates.length - 1] as Date,
    };
  }

  public addMonthToViewport(
    viewport1: IMonthlyCalendarViewport,
    viewport2: IMonthlyCalendarViewport,
  ): IMonthlyCalendarViewport {
    const monthGroups = [...viewport1.monthGroups, ...viewport2.monthGroups];

    const dates = this.#flattenMonthGroupsToDates(monthGroups);

    return {
      monthGroups,
      dates,
      startDate: dates[0] as Date,
      endDate: dates[dates.length - 1] as Date,
    };
  }

  #calculateDateRange(
    date: Date,
    numberOfMonths: number,
  ): { start: Date; end: Date } {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(addMonths(monthStart, numberOfMonths - 1));

    return {
      start: monthStart,
      end: monthEnd,
    };
  }

  #createMonthGroup(month: Date): IMonthGroup {
    const { weekStartsOn } = this;
    const monthStartDate = startOfMonth(month);
    const monthEndDate = endOfMonth(month);

    const viewportStart = startOfWeek(monthStartDate, { weekStartsOn });
    const viewportEnd = endOfWeek(monthEndDate, { weekStartsOn });

    const weekStarts = eachWeekOfInterval(
      { start: viewportStart, end: viewportEnd },
      { weekStartsOn },
    );

    const weeks = weekStarts.map((weekStart) => this.#createWeek(weekStart));

    return {
      monthLabel: formatDateFns(month, 'MMMM yyyy'),
      month,
      weeks,
    };
  }

  #createWeek(weekStart: Date): Date[] {
    const { weekStartsOn } = this;
    const weekEnd = endOfWeek(weekStart, { weekStartsOn });

    return eachDayOfInterval({ start: weekStart, end: weekEnd }).map((day) =>
      getCanonicalDate(day),
    );
  }

  #flattenMonthGroupsToDates(monthGroups: IMonthGroup[]): Date[] {
    return monthGroups.flatMap((group) => group.weeks.flat());
  }

  /**
   * generate a range of dates for calendar display
   */
  #generateDateRange(startDate: Date, numberOfDays: number): Date[] {
    const dates: Date[] = [];

    let date = startDate;

    for (let i = 0; i < numberOfDays; i++) {
      dates.push(getCanonicalDate(date));

      date = addDays(date, 1);
    }

    return dates;
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/utils': CalendarUtilsService;
  }
}
